import { Trash2 } from 'lucide-react';
import type { EquationDefinition } from 'used-vehicle-contracts';
import type { UnitName } from 'used-vehicle-engine';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { ExpressionEditor } from '../../../components/ui/ExpressionEditor';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { isValidCustomId, UNIT_OPTIONS } from './equations-display';

export interface EquationRowProps {
  equation: EquationDefinition;
  onChange: (patch: Partial<EquationDefinition>) => void;
  onRemove?: () => void;
  /** Present when the row cannot be removed, and says why. */
  removeBlockedBy?: readonly string[];
  result: string | null;
  error: string | null;
  disabled?: boolean;
}

/**
 * One equation in the editor.
 *
 * A SYSTEM equation's id is fixed — the estimator, the dashboard and every saved snapshot read it by
 * name — so it is shown as a mono badge and only the label and the expression are editable. A CUSTOM
 * `x_` intermediate is the administrator's own, so its id, unit and scope are editable too, and it can
 * be removed as soon as nothing refers to it.
 */
export const EquationRow = ({ equation, onChange, onRemove, removeBlockedBy, result, error, disabled = false }: EquationRowProps) => {
  const { t } = useI18nTranslation();
  const isCustom = equation.kind === 'CUSTOM';
  const idIsValid = !isCustom || isValidCustomId(equation.id);

  return (
    <div className="border-b border-border pb-5 last:border-b-0 last:pb-0">
      <ExpressionEditor
        id={`equation-${equation.id}`}
        value={equation.expression}
        disabled={disabled}
        onChange={(expression) => onChange({ expression })}
        result={result}
        error={error}
        header={
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {isCustom ? (
                <div className="w-44">
                  <Input
                    aria-label={t('equations.idField')}
                    dir="ltr"
                    value={equation.id}
                    disabled={disabled}
                    hasError={!idIsValid}
                    className="h-8 font-mono text-xs"
                    onChange={(event) => onChange({ id: event.target.value.trim() })}
                  />
                </div>
              ) : (
                <Badge mono>{equation.id}</Badge>
              )}

              <div className="min-w-0 flex-1">
                <Input
                  aria-label={t('equations.labelField')}
                  value={equation.label}
                  disabled={disabled}
                  placeholder={t('equations.labelField')}
                  className="h-8 text-sm"
                  onChange={(event) => onChange({ label: event.target.value })}
                />
              </div>

              {isCustom ? (
                <>
                  <Select
                    aria-label={t('equations.unitField')}
                    className="h-8 w-32 text-xs"
                    value={equation.unit}
                    disabled={disabled}
                    onChange={(event) => onChange({ unit: event.target.value as UnitName })}
                    options={UNIT_OPTIONS.map((unit) => ({ value: unit, label: t(`equations.units.${unit}`) }))}
                  />
                  <Select
                    aria-label={t('equations.scopeField')}
                    className="h-8 w-36 text-xs"
                    value={equation.scope}
                    disabled={disabled}
                    onChange={(event) => onChange({ scope: event.target.value as EquationDefinition['scope'] })}
                    options={[
                      { value: 'ALWAYS', label: t('equations.scopes.ALWAYS') },
                      { value: 'TRADE_IN', label: t('equations.scopes.TRADE_IN') },
                    ]}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled || (removeBlockedBy?.length ?? 0) > 0}
                    title={(removeBlockedBy?.length ?? 0) > 0 ? t('equations.removeBlocked', { ids: removeBlockedBy!.join(', ') }) : undefined}
                    onClick={onRemove}
                  >
                    <Trash2 className="size-3.5" />
                    {t('actions.remove')}
                  </Button>
                </>
              ) : (
                <Badge>{t(`equations.units.${equation.unit}`)}</Badge>
              )}

              {equation.scope === 'TRADE_IN' && !isCustom ? <Badge tone="info">{t('equations.scopes.TRADE_IN')}</Badge> : null}
              {equation.costCategory ? <Badge>{t('equations.resolvedFromCosts')}</Badge> : null}
            </div>

            {isCustom && !idIsValid ? <p className="text-xs text-destructive">{t('equations.customIdInvalid')}</p> : null}
          </div>
        }
        hint={equation.description}
      />

      {(removeBlockedBy?.length ?? 0) > 0 ? (
        <p className="mt-1.5 text-xs text-muted-foreground">{t('equations.removeBlocked', { ids: removeBlockedBy!.join(', ') })}</p>
      ) : null}
    </div>
  );
};
